import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/hooks/useOrganization';

export interface TeamMember {
  id: string;
  name: string;
  email: string; // Masked email from team_members_safe view
  email_masked: string | null; // Masked email from view
  role: string;
  department: string | null;
  skills: string[];
  capacity: number;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  organization_id: string | null;
}

export interface CreateTeamMemberData {
  name: string;
  email: string;
  role?: string;
  department?: string;
  skills?: string[];
  capacity?: number;
}

export function useTeamMembers() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { organization } = useOrganization();

  const fetchTeamMembers = async () => {
    if (!organization?.id) {
      setTeamMembers([]);
      setLoading(false);
      return;
    }

    try {
      // Use team_members_safe view to get masked emails for privacy
      const { data, error } = await supabase
        .from('team_members_safe')
        .select('*')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      // Map email_masked to email for display consistency
      const mappedData = (data || []).map(member => ({
        ...member,
        email: member.email_masked || '', // Use masked email for display
      })) as TeamMember[];
      setTeamMembers(mappedData);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch team members.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const createTeamMember = async (data: CreateTeamMemberData) => {
    if (!organization?.id) {
      toast({
        title: 'Error',
        description: 'No organization found. Please complete onboarding first.',
        variant: 'destructive'
      });
      return false;
    }

    // Validate input
    if (!data.name?.trim()) {
      toast({
        title: 'Error',
        description: 'Name is required.',
        variant: 'destructive'
      });
      return false;
    }

    if (!data.email?.trim()) {
      toast({
        title: 'Error',
        description: 'Email is required.',
        variant: 'destructive'
      });
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email.trim())) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address.',
        variant: 'destructive'
      });
      return false;
    }

    try {
      const { error } = await supabase.from('team_members').insert({
        ...data,
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        organization_id: organization.id
      });

      if (error) {
        // Check for duplicate email
        if (error.code === '23505') {
          toast({
            title: 'Error',
            description: 'A team member with this email already exists.',
            variant: 'destructive'
          });
          return false;
        }
        throw error;
      }
      
      toast({ title: 'Success', description: 'Team member added successfully.' });
      await fetchTeamMembers();
      return true;
    } catch (error) {
      console.error('Error creating team member:', error);
      toast({
        title: 'Error',
        description: 'Failed to add team member. Please try again.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const updateTeamMember = async (id: string, updates: Partial<TeamMember>) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      
      toast({ title: 'Success', description: 'Team member updated successfully.' });
      await fetchTeamMembers();
      return true;
    } catch (error) {
      console.error('Error updating team member:', error);
      toast({
        title: 'Error',
        description: 'Failed to update team member.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const deleteTeamMember = async (id: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
      
      toast({ title: 'Success', description: 'Team member removed successfully.' });
      await fetchTeamMembers();
      return true;
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove team member.',
        variant: 'destructive'
      });
      return false;
    }
  };

  useEffect(() => {
    if (organization?.id) {
      fetchTeamMembers();
    }

    // Subscribe to realtime updates
    const channel = supabase
      .channel('team-members-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'team_members' },
        () => {
          if (organization?.id) {
            fetchTeamMembers();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organization?.id]);

  return {
    teamMembers,
    loading,
    fetchTeamMembers,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember
  };
}
